import { useEffect, useReducer, type FC } from 'react';
import { useFormatters } from '@/hooks/useFormatters';

interface Props {
  timestamp: number;
  /** Formato corto («hace 5 s», «hace 3 min») y «ahora» por debajo de 5 s. */
  short?: boolean;
}

const SHORT_OPTIONS: Intl.RelativeTimeFormatOptions = { numeric: 'auto', style: 'short' };
const NOW_THRESHOLD_MS = 5000;

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

/**
 * Cuánto falta para el próximo segundo, minuto u hora cumplido. Desde un día el texto cambia a
 * medianoche, que puede verse hasta una hora tarde.
 */
const msUntilNextChange = (elapsed: number) => {
  const step = elapsed < MINUTE ? SECOND : elapsed < HOUR ? MINUTE : HOUR;
  return step - (elapsed % step);
};

const TimeElapsed: FC<Props> = ({ timestamp, short = false }) => {
  const { formatRelativeTime } = useFormatters();
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const now = Date.now();
  // Un timestamp apenas adelantado al reloj local es desfase entre relojes, no futuro: «ahora».
  const elapsed = Math.max(0, now - timestamp);

  // Sin dependencias: se reprograma en cada render, incluso cuando `elapsed` se repite (futuro → 0).
  useEffect(() => {
    const timeout = setTimeout(rerender, msUntilNextChange(elapsed));
    return () => clearTimeout(timeout);
  });

  if (!short) return formatRelativeTime(now - elapsed, null, null, now);
  // `numeric: 'auto'` sobre 0 segundos da «ahora» ya localizado.
  if (elapsed < NOW_THRESHOLD_MS) return formatRelativeTime(0, 'second', SHORT_OPTIONS, now);
  return formatRelativeTime(now - elapsed, null, SHORT_OPTIONS, now);
};

export default TimeElapsed;
